import { useEffect, useState } from 'react'
import apiClient from '../../../api/index.js'
import { asMessage } from '../../../shared/utils/errorDetail'
import { parseUTC } from '../../../utils/dateUtils'

/**
 * PLATFORM ADMIN, READ-ONLY: what has been done to companies, newest first (identity GET
 * /platform/activity). Companies created, suspended or reactivated, modules switched, paid add-ons and
 * features changed, sending domains set up, each company's own AI consent changes, and - when the
 * database has it - the Office Worker's grant and key log. Filter by company and by kind; 25 a page.
 *
 * The server words every row in one sentence, so this screen only lays them out.
 */
const PAGE_SIZE = 25

const when = (at) => {
  const d = at ? parseUTC(at) : null
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : ''
}

export default function PlatformActivity({ tenants = [] }) {
  const [tenantId, setTenantId] = useState('')
  const [kind, setKind] = useState('')
  const [page, setPage] = useState(1)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [reloadKey, setReloadKey] = useState(0)   // bumped by "Try again"

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError('')
      try {
        const params = { page, page_size: PAGE_SIZE }
        if (tenantId) params.tenant_id = tenantId
        if (kind) params.kind = kind
        const res = await apiClient.get('/platform/activity', { params })
        if (!cancelled) setData(res.data)
      } catch (err) {
        // A failed load shows ONLY the failure (review SA-4): the rows and page count of the previous
        // filter must not stay under a new one, where they read as that company's changes.
        if (!cancelled) {
          setData(null)
          setError(asMessage(err?.response?.data?.detail) || 'Could not load the activity.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [tenantId, kind, page, reloadKey])

  const kinds = data?.kinds || {}
  const pages = data ? Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE)) : 1
  const items = data?.items || []

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Activity</h2>
        <p className="text-gray-500 text-sm mt-1">What has been changed for each company, newest first. Read-only.</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <label className="text-sm text-gray-700">
          Company{' '}
          <select value={tenantId} onChange={(e) => { setTenantId(e.target.value); setPage(1) }}
                  className="ml-1 border border-gray-300 rounded-md px-2 py-1 text-sm" aria-label="Company">
            <option value="">All companies</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
        <label className="text-sm text-gray-700">
          Kind{' '}
          <select value={kind} onChange={(e) => { setKind(e.target.value); setPage(1) }}
                  className="ml-1 border border-gray-300 rounded-md px-2 py-1 text-sm" aria-label="Kind">
            <option value="">Everything</option>
            {Object.entries(kinds).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-[48rem] w-full text-left text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-500 text-xs font-semibold uppercase tracking-wider border-b border-gray-200">
              <th className="p-3">When</th>
              <th className="p-3">Company</th>
              <th className="p-3">What</th>
              <th className="p-3">By</th>
              <th className="p-3">Why</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan="5" className="p-6 text-center text-gray-500">Loading activity…</td></tr>
            ) : error ? (
              <tr><td colSpan="5" className="p-6 text-center">
                <span className="text-sm text-red-600">{error}</span>{' '}
                <button type="button" onClick={() => setReloadKey((k) => k + 1)}
                        className="text-sm font-medium text-blue-600 hover:text-blue-700">Try again</button>
              </td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan="5" className="p-6 text-center text-gray-500">Nothing recorded yet.</td></tr>
            ) : items.map((i) => (
              <tr key={i.id} data-testid="activity-row">
                <td className="p-3 whitespace-nowrap text-gray-600">{when(i.at)}</td>
                <td className="p-3 text-gray-900">{i.tenant_name || '-'}</td>
                <td className="p-3 text-gray-900">{i.summary}</td>
                <td className="p-3 text-gray-600">{i.by || '-'}</td>
                <td className="p-3 text-gray-600">{i.note || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>{data && !error ? `Page ${page} of ${pages} - ${data.total} in all` : ''}</span>
        <div className="flex gap-2">
          <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}
                  className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50">Newer</button>
          <button type="button" disabled={page >= pages || loading} onClick={() => setPage((p) => p + 1)}
                  className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50">Older</button>
        </div>
      </div>
      {data?.office_worker_log && data.office_worker_log.startsWith('left out') && (
        <p className="text-xs text-gray-400">Office Worker grant and key changes are not in this list: this database has no record of them yet.</p>
      )}
    </div>
  )
}
